import fscreen from 'fscreen';
import { Map } from 'immutable';
import { HalLink } from 'libs/shared/contract-common/src/lib/mode-api';
import { mapKeys } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { PdfExportHelpers } from '@mode/legacy/data-access-model-js';

import { DatasetSelectors } from '@mode/dataset/domain';
import { GithubSelectors } from '@mode/github/domain';
import {
  AccountResourceHelper,
  ReportRunResource,
  ReportRunResourceHelpers,
  SpaceHelpers,
} from '@mode/legacy/data-access-model-js';
import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { AppState, MenuOptionId, MenuOptionRecord, RouterStateHelpers } from '@mode/legacy/types-app-state';
import { headerReportData } from '@mode/reports/domain';
import {
  accountContextSelector,
  accountContextSpacesSelector,
  accountOrganizationsSelector,
  accountSelector,
  currentUserBelongsToReportContextSelector,
  loggedInStateSelector,
  reportContextSelector,
  routerSelector,
  selectedReportReportRunPdfExportSelector,
  selectedReportReportRunResourceSelector,
  selectedReportReportRunStateSelector,
  selectedReportSpaceSelector,
} from '../reducers/selectors';
import { ApiFeatureSelectors } from './api-feature.selectors';
import { getThoughtspotIntegrated } from '@mode/shared/util-js';

type MenuOptionStructuredSelector = {
  [K in MenuOptionId.Report]: MenuOptionRecord<K>;
};

export namespace PlatformHeaderSelectors {
  const { getCsvLink, hasCsvLink, canEmbed } = ReportRunResourceHelpers;
  const { isEmailVerified } = AccountResourceHelper;
  const { isEditor } = RouterStateHelpers;
  const { isOpen } = SpaceHelpers;

  export const getOptionDelete = createSelector(headerReportData, (report) => {
    const name = 'Delete\u2026';
    let enabled = false;
    let granted = false;
    if (report) {
      enabled = !!report?.last_run_at;
      granted = !!report?._forms?.['destroy'];
    }

    const href = '';

    return new MenuOptionRecord({ id: MenuOptionId.Report.Delete, name, enabled, granted, href });
  });

  export const getOptionEdit = createSelector(
    headerReportData,
    ApiFeatureSelectors.getCanEditSelectedReport,
    (report, canEditReport) => {
      const name = 'Edit';
      const enabled = true;
      const granted = canEditReport;
      const link = report?._links?.['web_edit'] as HalLink;
      const href = link?.href ?? '';

      return new MenuOptionRecord<MenuOptionId.Report.Edit>({
        id: MenuOptionId.Report.Edit,
        name,
        enabled,
        granted,
        href,
      });
    }
  );

  export const getOptionExport = createSelector(
    selectedReportReportRunResourceSelector,
    loggedInStateSelector,
    (reportRun, loggedIn) => {
      const name = 'Export';
      const enabled = true;
      const granted = reportRun != null && loggedIn;

      const href = '';

      return new MenuOptionRecord({ id: MenuOptionId.Report.Export, name, enabled, granted, href });
    }
  );

  export const getOptionExportAll = createSelector(
    selectedReportReportRunResourceSelector,
    loggedInStateSelector,
    (reportRun, loggedIn) => {
      const name = 'Export All';
      const href = getCsvLink(reportRun);
      const granted = hasCsvLink(reportRun) && !loggedIn;
      const enabled = !!href;

      return new MenuOptionRecord({ id: MenuOptionId.Report.ExportAll, name, enabled, granted, href });
    }
  );

  export const getOptionExportPdf = createSelector(
    selectedReportReportRunPdfExportSelector,
    selectedReportReportRunStateSelector,
    headerReportData,
    (pdfExport, reportRun, report) => {
      const hasNotBeenExported = !PdfExportHelpers.reportHasBeenExportedToPdf(pdfExport);
      const analystStudio = getThoughtspotIntegrated(document);
      const hasRunAndNotBeenExported = !!(report?.last_run_at && reportRun && hasNotBeenExported);

      const enabled = reportRun != null && !reportRun.loading;
      const granted = report != null && hasRunAndNotBeenExported && !analystStudio;

      return new MenuOptionRecord({ id: MenuOptionId.Report.ExportPdf, name: 'PDF', enabled, granted, href: '' });
    }
  );

  export const getOptionExportPdfDownload = createSelector(
    selectedReportReportRunPdfExportSelector,
    selectedReportReportRunStateSelector,
    headerReportData,
    (pdfExport, reportRun, report) => {
      const name = 'PDF';
      const hasBeenExported = PdfExportHelpers.reportHasBeenExportedToPdf(pdfExport);
      const enabled = reportRun != null && !reportRun.loading;
      const granted = report != null && !!report.last_run_at && hasBeenExported;

      const href = hasBeenExported ? pdfExport.links('download').href : '';

      return new MenuOptionRecord({ id: MenuOptionId.Report.ExportPdfDownload, name, enabled, granted, href });
    }
  );

  export const getOptionFullscreen = createSelector(headerReportData, (report) => {
    const name = 'Fullscreen';
    const enabled = fscreen.fullscreenEnabled; // don't enable fullscreen button if browser doesn't support it
    const granted = true;

    return new MenuOptionRecord({ id: MenuOptionId.Report.Fullscreen, name, enabled, granted, href: '' });
  });

  export const getOptionMove = createSelector(headerReportData, (report) => {
    const name = 'Move to\u2026';
    const enabled = !!report?.last_run_at;
    const granted = true;

    return new MenuOptionRecord({ id: MenuOptionId.Report.Move, name, enabled, granted, href: '' });
  });

  export const getOptionRename = createSelector(headerReportData, (report) => {
    const name = 'Rename';
    const enabled = !!report?.last_run_at;
    const granted = enabled;

    return new MenuOptionRecord({ id: MenuOptionId.Report.Rename, name, enabled, granted, href: '' });
  });

  export const getOptionRun = createSelector(headerReportData, (report) => {
    const name = 'Run Now';
    // NOTE: previous reducer logic left this enabled when there's no report as long as the previous state was enabled
    const enabled = report && !report.manual_run_disabled;
    const granted = !!report?._links?.['web_report_runs'];

    return new MenuOptionRecord({ id: MenuOptionId.Report.Run, name, enabled, granted, href: '' });
  });

  export const getOptionRefresh = createSelector(headerReportData, (report) => {
    const name = 'Refresh';
    // NOTE: previous reducer logic left this enabled when there's no report as long as the previous state was enabled
    const enabled = report && !report.manual_run_disabled;
    const granted = !!report?._links?.['web_report_runs'];

    return new MenuOptionRecord({ id: MenuOptionId.Report.Refresh, name, enabled, granted, href: '' });
  });

  export const getOptionSave = createSelector(headerReportData, (report) => {
    const name = 'Save changes';
    const enabled = !!report?.last_run_at;
    const granted = true;

    return new MenuOptionRecord({ id: MenuOptionId.Report.Save, name, enabled, granted, href: '' });
  });

  export const getOptionSettings = createSelector(headerReportData, (report) => {
    const name = 'Settings';
    const enabled = !!report?.last_run_at;
    const granted = true;

    return new MenuOptionRecord({ id: MenuOptionId.Report.Settings, name, enabled, granted, href: '' });
  });

  export const getOptionShare = createSelector(
    headerReportData,
    selectedReportReportRunStateSelector,
    currentUserBelongsToReportContextSelector,
    routerSelector,
    (report, reportRun, belongsToReportContext, router) => {
      const name = 'Share';
      const reportLastRunAt = report && report.last_run_at;
      const isEditor = RouterStateHelpers.isEditor(router);
      const allDataPresent = !!(reportLastRunAt && reportRun && belongsToReportContext && report);

      let reportIsEligibleForSharing = false;
      let isCommunityReport = false;
      let userCanShareReportView = isEditor;

      if (allDataPresent) {
        reportIsEligibleForSharing = !!(reportLastRunAt && reportRun.resource && reportRun.resource.hasLink('share'));
        isCommunityReport = report.public;
        userCanShareReportView = isEditor || reportIsEligibleForSharing;
      }

      const enabled = reportIsEligibleForSharing;
      const granted = belongsToReportContext && userCanShareReportView && (!isCommunityReport || isEditor);

      return new MenuOptionRecord({ id: MenuOptionId.Report.Share, name, enabled, granted, href: '' });
    }
  );

  export const getOptionSimpleShare = createSelector(headerReportData, (report) => {
    const name = 'Share';
    const enabled = (report && report.public) || false;
    const granted = enabled;

    return new MenuOptionRecord({ id: MenuOptionId.Report.SimpleShare, name, enabled, granted, href: '' });
  });

  export const getOptionStarReport = createSelector(headerReportData, (report) => {
    const name = 'Star Report';
    const enabled = true;
    const granted = !!report?._embedded?.['new_star'];

    return new MenuOptionRecord({ id: MenuOptionId.Report.StarReport, name, enabled, granted, href: '' });
  });

  export const getOptionViewDetails = createSelector(headerReportData, routerSelector, (report, router) => {
    const name = 'View Details';
    const enabled = report != null;
    const granted = !RouterStateHelpers.isReportDetails(router);

    return new MenuOptionRecord({ id: MenuOptionId.Report.ViewDetails, name, enabled, granted, href: '' });
  });

  export const getOptionViewReport = createSelector(headerReportData, routerSelector, (report, router) => {
    const name = 'View Report';

    const isReportDetailsPage: boolean = RouterStateHelpers.isReportDetails(router);
    const isEditor = RouterStateHelpers.isEditor(router);
    const isNotebook = RouterStateHelpers.isNotebook(router);
    const hasBeenRun = !!report?.last_run_at;

    const isAnalystStudio = getThoughtspotIntegrated(document);
    const enabled = isEditor ? hasBeenRun : true;
    const granted = isAnalystStudio ? false : isReportDetailsPage || isEditor || isNotebook;

    return new MenuOptionRecord({ id: MenuOptionId.Report.ViewReport, name, enabled, granted, href: '' });
  });

  export const getOptionClone = createSelector(
    headerReportData,
    selectedReportReportRunStateSelector,
    loggedInStateSelector,
    (report, reportRun, isLoggedIn) => {
      const name = 'Duplicate';
      const enabled = !!(report && reportRun && reportRun.resource);
      const currentUserCanCloneReport = !!(reportRun && reportRun.resource && reportRun.resource.hasLink('web_clone'));
      const granted = isLoggedIn && currentUserCanCloneReport;
      const href = '';

      return new MenuOptionRecord<MenuOptionId.Report.Clone>({
        id: MenuOptionId.Report.Clone,
        name,
        enabled,
        granted,
        href,
      });
    }
  );

  export const getOptionCloneLoggedOut = createSelector(
    headerReportData,
    selectedReportReportRunStateSelector,
    loggedInStateSelector,
    (report, reportRun, isLoggedIn) => {
      const name = 'Duplicate';
      const reportIsPublic = !!(report && report.public);
      const enabled = !!(report && reportRun && reportRun.resource);
      const granted = !isLoggedIn && reportIsPublic;
      const href = '';

      return new MenuOptionRecord<MenuOptionId.Report.CloneLoggedOut>({
        id: MenuOptionId.Report.CloneLoggedOut,
        name,
        enabled,
        granted,
        href,
      });
    }
  );

  export const getOptionSchedule = createSelector(
    headerReportData,
    accountSelector,
    ApiFeatureSelectors.getCanCreateSelectedReportSubscription,
    (report, account, canCreateReportSubscription) => {
      const name = 'Schedule';
      const granted = canCreateReportSubscription && isEmailVerified(account) && !report?.public;
      const enabled = !!report?.last_run_at;
      return new MenuOptionRecord<MenuOptionId.Report.Schedule>({
        id: MenuOptionId.Report.Schedule,
        name,
        enabled,
        granted,
      });
    }
  );

  export const getOptionSubscribe = createSelector(
    headerReportData,
    currentUserBelongsToReportContextSelector,
    ApiFeatureSelectors.getCanCreateSelectedReportSubscription,
    (report, belongsToReportContext, canCreateReportSubscription) => {
      const name = 'Schedule';
      const granted = belongsToReportContext && canCreateReportSubscription && !report?.public;
      const enabled = !!report?.last_run_at;
      return new MenuOptionRecord<MenuOptionId.Report.Subscribe>({
        id: MenuOptionId.Report.Subscribe,
        name,
        enabled,
        granted,
      });
    }
  );

  export const getOptionExportCsv = createSelector(selectedReportReportRunStateSelector, (reportRunState) => {
    const name = 'CSV';
    const reportRun: ReportRunResource = reportRunState && reportRunState.resource;
    const href = getCsvLink(reportRun);
    const granted = hasCsvLink(reportRun);
    const enabled = !!href;

    return new MenuOptionRecord<MenuOptionId.Report.ExportCsv>({
      id: MenuOptionId.Report.ExportCsv,
      name,
      enabled,
      granted,
      href,
    });
  });

  export const getOptionManageReport = createSelector(accountSelector, (account) => {
    const name = 'Report';
    const href = '';
    const enabled = account != null;
    const granted = enabled;

    return new MenuOptionRecord({
      id: MenuOptionId.Report.ManageReport,
      name,
      granted: granted,
      enabled: true,
      href: href,
    });
  });

  export const getOptionSync = createSelector(
    GithubSelectors.isAuthorizedToSyncToGithub,
    GithubSelectors.isSyncToGithubEnabled,
    DatasetSelectors.selectIsSheetDataset,
    (isAuthorizedToSyncToGithub, isSyncToGithubEnabled, isSheetDataset) => {
      const name = 'Push changes to GitHub';
      const enabled = isSyncToGithubEnabled;
      const granted = isAuthorizedToSyncToGithub && !isSheetDataset;
      return new MenuOptionRecord<MenuOptionId.Report.Sync>({ id: MenuOptionId.Report.Sync, name, enabled, granted });
    }
  );

  export const getOptionViewGithub = createSelector(GithubSelectors.githubLink, (githubLink) => {
    const name = 'View changes in GitHub';

    return new MenuOptionRecord<MenuOptionId.Report.ViewGithub>({
      id: MenuOptionId.Report.ViewGithub,
      name,
      granted: !!githubLink,
      enabled: true,
      href: githubLink,
    });
  });

  export const getOptionEmbed = createSelector(
    headerReportData,
    selectedReportReportRunStateSelector,
    currentUserBelongsToReportContextSelector,
    routerSelector,
    (report, reportRunState, belongsToReportContext, router) => {
      const name = 'Embed';
      const reportRun: ReportRunResource = reportRunState && reportRunState.resource;
      const granted = belongsToReportContext && canEmbed(reportRun) && (!report?.public || isEditor(router));
      const enabled = !!report?.last_run_at;

      return new MenuOptionRecord<MenuOptionId.Report.Embed>({ id: MenuOptionId.Report.Embed, name, enabled, granted });
    }
  );

  export const getOptionCreateShortcut = createSelector(
    headerReportData,
    selectedReportSpaceSelector,
    (report, space) => {
      const name = 'Create shortcut\u2026';
      const granted = !!report?._embedded?.['new_space_link'] && isOpen(space);
      const enabled = !!report?.last_run_at;

      return new MenuOptionRecord<MenuOptionId.Report.CreateShortcut>({
        id: MenuOptionId.Report.CreateShortcut,
        name,
        enabled,
        granted,
      });
    }
  );

  export const getOptionsById = createStructuredSelector<AppState, MenuOptionStructuredSelector>({
    [MenuOptionId.Report.Clone]: getOptionClone,
    [MenuOptionId.Report.CloneLoggedOut]: getOptionCloneLoggedOut,
    [MenuOptionId.Report.CreateShortcut]: getOptionCreateShortcut,
    [MenuOptionId.Report.Delete]: getOptionDelete,
    [MenuOptionId.Report.Edit]: getOptionEdit,
    [MenuOptionId.Report.Embed]: getOptionEmbed,
    [MenuOptionId.Report.Export]: getOptionExport,
    [MenuOptionId.Report.ExportAll]: getOptionExportAll,
    [MenuOptionId.Report.ExportCsv]: getOptionExportCsv,
    [MenuOptionId.Report.ExportPdf]: getOptionExportPdf,
    [MenuOptionId.Report.ExportPdfDownload]: getOptionExportPdfDownload,
    [MenuOptionId.Report.Fullscreen]: getOptionFullscreen,
    [MenuOptionId.Report.ManageReport]: getOptionManageReport,
    [MenuOptionId.Report.Move]: getOptionMove,
    [MenuOptionId.Report.Rename]: getOptionRename,
    [MenuOptionId.Report.Refresh]: getOptionRefresh,
    [MenuOptionId.Report.Run]: getOptionRun,
    [MenuOptionId.Report.Save]: getOptionSave,
    [MenuOptionId.Report.Schedule]: getOptionSchedule,
    [MenuOptionId.Report.Settings]: getOptionSettings,
    [MenuOptionId.Report.Share]: getOptionShare,
    [MenuOptionId.Report.SimpleShare]: getOptionSimpleShare,
    [MenuOptionId.Report.StarReport]: getOptionStarReport,
    [MenuOptionId.Report.Subscribe]: getOptionSubscribe,
    [MenuOptionId.Report.Sync]: getOptionSync,
    [MenuOptionId.Report.ViewDetails]: getOptionViewDetails,
    [MenuOptionId.Report.ViewGithub]: getOptionViewGithub,
    [MenuOptionId.Report.ViewReport]: getOptionViewReport,
  });

  export const menuOptionIndexByIdSelector = createSelector(
    getOptionsById,
    (actions) =>
      Map(mapKeys(actions, (action) => action.id)) as Map<MenuOptionId.Report, MenuOptionRecord<MenuOptionId.Report>>
  );

  /**
   * parameterized selector factories that take non importable
   * arguments that are only available at run-time.
   * @todo (Franklin) 5/25/2018: Remove once platform header no longer depends on models
   */
  export namespace SelectorFactories {
    import resourcesToModels = ModeResourceHelper.resourcesToModels;
    import resourceToModel = ModeResourceHelper.resourceToModel;

    export function orgSpaces(SpaceModel) {
      return createSelector(accountContextSpacesSelector, (spaces) => resourcesToModels(spaces, SpaceModel.model));
    }

    export function orgContext(AccountModel) {
      return createSelector(reportContextSelector, (account) => resourceToModel(account, AccountModel.model));
    }

    export function accountContext(AccountModel) {
      return createSelector(accountContextSelector, (account) => resourceToModel(account, AccountModel.model));
    }

    export function orgs(OrgModel) {
      return createSelector(accountOrganizationsSelector, (organizations) =>
        resourcesToModels(organizations, OrgModel.model)
      );
    }

    export function account(AccountModel) {
      return createSelector(accountSelector, (account) => resourceToModel(account, AccountModel.model));
    }
  }
}
